import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

export type TravelStyle = 'budget' | 'luxury' | 'balanced';
export type TripType = 'adventure' | 'relaxation' | 'mixed';
export type TravelerType = 'solo' | 'family' | 'business' | 'couple';

interface UserPreferences {
  travel_style?: TravelStyle;
  trip_type?: TripType;
  traveler_type?: TravelerType;
}

export function useUserPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>({
    travel_style: 'balanced',
    trip_type: 'mixed',
    traveler_type: 'solo'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    } else {
      // Load from localStorage for non-authenticated users
      const saved = localStorage.getItem('travel_preferences');
      if (saved) {
        try {
          setPreferences(JSON.parse(saved));
        } catch (e) {
          console.error('Error parsing saved preferences:', e);
        }
      }
      setLoading(false);
    }
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPreferences({
          travel_style: (data as any).travel_style as TravelStyle || 'balanced',
          trip_type: (data as any).trip_type as TripType || 'mixed',
          traveler_type: (data as any).traveler_type as TravelerType || 'solo'
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (newPreferences: Partial<UserPreferences>) => {
    const updated = { ...preferences, ...newPreferences };
    setPreferences(updated);

    if (user) {
      // Save to database for authenticated users
      try {
        const { error } = await supabase
          .from('profiles')
          .update(newPreferences as any)
          .eq('id', user.id);

        if (error) throw error;
      } catch (error) {
        console.error('Error updating preferences:', error);
      }
    } else {
      // Save to localStorage for non-authenticated users
      localStorage.setItem('travel_preferences', JSON.stringify(updated));
    }
  };

  return {
    preferences,
    updatePreferences,
    loading
  };
}